import axios from 'axios';
import {ENDPOINT_URL, GET_ATF_PROFILE_LIST} from '../../../constants/commonConstants';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';

/**
 * Return a ATF profile row identifier.
 * @param {object} item Canvas ATF profile row
 * @return {*} identifier
 */
function rowId(item) {
	return item.profile_id ?? item.id;
}

/**
 * Normalize a Canvas ATF profile row.
 *
 * Only what names the row. The configuration itself - community strings,
 * passwords, the rest - stays on the form where the person can see it.
 *
 * @param {object} item Canvas ATF profile row
 * @return {object|null} agent candidate
 */
function normalizeRow(item) {
	const id = rowId(item);
	const name = item.profile_name ?? item.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'air_time_fairness',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Resolve ATF profile candidates.
 * This table has no server-side search, so every profile is listed and
 * matched here. There are only a few of them, which is why that is
 * affordable - and why no search capability is offered for it.
 *
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveAirTimeFairness(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = '';
		const url = `${ENDPOINT_URL}${GET_ATF_PROFILE_LIST}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('air_time_fairness', rows, rowId);
		return {
			candidates: rows.map(normalizeRow).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('air_time_fairness', rows, rowId);
	return {candidates};
}

/**
 * Return a retained row, for the page to act on.
 *
 * Retained when the profile is resolved rather than read off the table,
 * because the table is still loading when the command that needs it
 * arrives - the section it lives in has only just been opened.
 *
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedAirTimeFairness(entityId) {
	return getResolvedEntity('air_time_fairness', entityId);
}
